import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, DatePicker, Empty, Select, Table, Tooltip } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import BaseForm from '../../../components/form/BaseForm';
import SearchBox from '../../../components/outline/SearchBox';
import { FeatureFlagHistory } from '../../../lib/api/featureFlag';
import { SettingsSection } from '../../pgProviderRoute/common/SettingsSection';
import { formatLogDate } from '../../pgProviderRoute/utils/formatLogDate';
import { featureFlagQuery } from '../queries';
import { FLAG_HISTORY_PAGE_SIZE, useFeatureFlagHistoryFilters } from '../hooks/useFeatureFlagHistoryFilters';
import { historyValueLabel } from '../utils/featureFlagLabels';
import { FeatureFlagStyle } from './styles';

export const FeatureFlagHistoryPanel = (): React.JSX.Element => {
  const filters = useFeatureFlagHistoryFilters();
  const query = useQuery({ ...featureFlagQuery.histories(filters.params), keepPreviousData: true });
  // 플래그 고르기 목록 — 목록 탭과 같은 캐시를 쓴다
  const listQuery = useQuery(featureFlagQuery.list());
  const flagOptions = [
    { value: filters.ALL, label: '전체 플래그' },
    ...(listQuery.data?.flags ?? []).map((flag) => ({
      value: flag.ticketKey,
      label: flag.ticketNumber ?? `${flag.ticketKey} (코드에 없음)`,
    })),
  ];

  const columns: ColumnsType<FeatureFlagHistory> = [
    { title: '변경 일시', dataIndex: 'createDate', width: 150, render: (date: string) => formatLogDate(date) },
    {
      title: '플래그',
      dataIndex: 'ticketKey',
      width: 140,
      render: (_, history) =>
        history.ticketNumber ? (
          <b>{history.ticketNumber}</b>
        ) : (
          <>
            <b>{history.ticketKey}</b> <span className="settings-muted">코드에 없음</span>
          </>
        ),
    },
    {
      title: '변경',
      width: 240,
      render: (_, history) => (
        <>
          <span className="settings-muted">{historyValueLabel(history.beforeEnabled)}</span> →{' '}
          <b>{historyValueLabel(history.afterEnabled)}</b>
        </>
      ),
    },
    {
      title: '변경자',
      dataIndex: 'adminName',
      width: 110,
      render: (name: string | null, history) => name ?? (history.adminId ? '관리자 #' + history.adminId : '-'),
    },
    { title: '사유', dataIndex: 'reason' },
    {
      title: '알림',
      dataIndex: 'notifiedInstances',
      width: 100,
      render: (count: number | null) =>
        count ? (
          `앱 ${count}개`
        ) : (
          <Tooltip title="알림을 받은 앱이 없었습니다 — 각 앱이 다시 연결되거나 재시작할 때 반영됩니다">
            <span className="flag-override">
              <WarningOutlined aria-hidden /> {count === 0 ? '0개' : '실패'}
            </span>
          </Tooltip>
        ),
    },
  ];

  return (
    <FeatureFlagStyle>
      <div className="settings-panel-header">
        <div>
          <h2>플래그 변경 이력</h2>
          <p>이 환경에서 바꾼 기록입니다. 값과 같은 트랜잭션으로 남아 저장 직후 바로 보입니다.</p>
        </div>
        <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => query.refetch()}>
          새로고침
        </Button>
      </div>
      <SearchBox onSearch={filters.apply} onClear={filters.reset}>
        <BaseForm
          label="플래그"
          content={
            <Select
              aria-label="이력 플래그"
              value={filters.ticketKey}
              options={flagOptions}
              onChange={filters.setTicketKey}
              style={{ minWidth: 200 }}
            />
          }
        />
        <BaseForm
          label="변경 일시"
          subContent="한국시간(KST) 기준 · 시작일과 종료일 모두 포함 · 비우면 전체 기간"
          content={
            <DatePicker.RangePicker
              aria-label="변경 일시 범위"
              placeholder={['시작일', '종료일']}
              format="YYYY-MM-DD"
              value={filters.dateRange}
              onChange={filters.setDateRange}
            />
          }
        />
      </SearchBox>
      {query.isError ? (
        <Alert type="error" showIcon message="변경 이력을 불러오지 못했습니다. 새로고침해 주세요." />
      ) : (
        <SettingsSection
          title="변경 기록"
          action={<span className="settings-muted">{query.data?.total ?? 0}건 · 최신순</span>}
        >
          <Table
            rowKey="id"
            columns={columns}
            dataSource={query.data?.data ?? []}
            loading={query.isFetching}
            pagination={{
              current: filters.page,
              pageSize: FLAG_HISTORY_PAGE_SIZE,
              total: query.data?.total ?? 0,
              onChange: filters.setPage,
              showSizeChanger: false,
              hideOnSinglePage: true,
            }}
            scroll={{ x: 900 }}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="표시할 변경 이력이 없습니다." />,
            }}
          />
        </SettingsSection>
      )}
    </FeatureFlagStyle>
  );
};
